import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/RequestRateLimitsTableRow.css';
import { requestRateLimitType } from '../../types';
import { getRequestType } from '../../utils/RequestTypes';
import NumberField from '../fields/NumberField';
import useExtendedState from '../../hooks/useExtendedState';
import Constants from '../../utils/Constants';
import useSingleValidator from '../../hooks/useSingleValidator';
import { settingsRateLimitValidator } from '../../validators/settings';
import ConfirmButton from '../buttons/ConfirmButton';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import ReadonlyNumberField from '../fields/ReadonlyNumberField';

export const formatUserForDisplay = (t, user) => (
    user === null
        ? t('settings.request-rates-and-limits.request-rate-limits.all-users')
        : `${user.name} (${user.racf})`
);

RequestRateLimitsTableRow.propTypes = {
    requestRateLimit: requestRateLimitType.isRequired,
    onRequestRateLimitDelete: PropTypes.func.isRequired,
    onRequestRateLimitValidated: PropTypes.func.isRequired,
    backendChanged: PropTypes.bool.isRequired,
    doReset: PropTypes.bool.isRequired,
};

function RequestRateLimitsTableRow(props) {
    const {
        requestRateLimit,
        onRequestRateLimitDelete,
        onRequestRateLimitValidated,
        backendChanged,
        doReset,
    } = props;
    const { t } = useTranslation();
    const rateLimitValueState = useExtendedState(requestRateLimit.value);
    const {
        validator,
        reset: resetValidator,
    } = useSingleValidator(true, useMemo(() => settingsRateLimitValidator(t, Constants.MIN_RATE_LIMIT_VALUE, Constants.MAX_RATE_LIMIT_VALUE), [t]));
    const deleteRequestRateLimit = () => {
        onRequestRateLimitDelete(requestRateLimit.user, requestRateLimit.requestType);
    };
    const onRateLimitValidated = (newRateLimitValue, isValid) => {
        onRequestRateLimitValidated(newRateLimitValue, isValid);
    };
    useEffectOnUpdate(() => {
        if (doReset) {
            resetValidator();
            rateLimitValueState.reset();
        }
    }, [doReset]);
    useEffectOnUpdate(() => {
        if (backendChanged) {
            rateLimitValueState.changeInitialState(rateLimitValueState.value);
            rateLimitValueState.reset();
        }
    }, [backendChanged, rateLimitValueState.value]);
    let deleteRequestRateLimitConfirmMessage;
    if (requestRateLimit.user) {
        deleteRequestRateLimitConfirmMessage = t('settings.request-rates-and-limits.request-rate-limits.confirm-delete-request-rate-limit', { user: requestRateLimit.user });
    } else {
        deleteRequestRateLimitConfirmMessage = t('settings.request-rates-and-limits.request-rate-limits.confirm-delete-request-rate-limit-for-all');
    }
    return (
        <div className="detail-list-row request-rate-limit-table-row">
            <div className="request-rate-limit-cell--small request-rate-limits-table-row--remove">
                {requestRateLimit.limitId && (
                    <ConfirmButton
                        type="button"
                        className="delete-button button-small button-bordered"
                        onConfirm={deleteRequestRateLimit}
                        message={deleteRequestRateLimitConfirmMessage}
                    >
                        <FontAwesomeIcon icon="times" />
                    </ConfirmButton>
                )}
            </div>
            <div className="request-rate-limit-cell request-rate-limits-table-row--user readonly">{formatUserForDisplay(t, requestRateLimit.user)}</div>
            <div className="request-rate-limit-cell request-rate-limits-table-row--request-type readonly">{getRequestType(requestRateLimit.requestType).translate(t)}</div>
            <div className="request-rate-limit-cell request-rate-limits-table-row--message-rate">
                <ReadonlyNumberField
                    value={requestRateLimit.messageRate}
                    className="request-rate-limits-table-row--message-rate--value"
                />
            </div>
            <div className="request-rate-limit-cell request-rate-limits-table-row--rate-limit">
                <NumberField
                    state={rateLimitValueState}
                    className="request-rate-limits-table-row--rate-limit--value"
                    onValidated={onRateLimitValidated}
                    step={Constants.RATE_LIMIT_VALUE_STEP}
                    min={Constants.MIN_RATE_LIMIT_VALUE}
                    max={Constants.MAX_RATE_LIMIT_VALUE}
                    decrementDisabled={rateLimitValueState.value - Constants.RATE_LIMIT_VALUE_STEP < Constants.MIN_RATE_LIMIT_VALUE}
                    incrementDisabled={rateLimitValueState.value + Constants.RATE_LIMIT_VALUE_STEP > Constants.MAX_RATE_LIMIT_VALUE}
                    validator={validator}
                />
            </div>
        </div>
    );
}

export default RequestRateLimitsTableRow;
